import { useState, useEffect } from "react";
import { Search, Loader2, X, FileText } from "lucide-react";
import { adminApi } from "../../api";
import useTransitionModal from "./useTransitionModal";
import useBillingModal from "./useBillingModal";
import TransitionModal from "./TransitionModal";
import BillingModal from "./BillingModal";

const EUR = (v) =>
  v == null ? "—" : new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(Number(v));

/**
 * Quick search widget for "Tratar Encomendas". Lives OUTSIDE individual
 * state filters: matches across all states by ID (encomenda / cliente /
 * fatura). Each result row offers the SAME actions as the per-state
 * workflow page — state transition modal + billing/Moloni docs modal —
 * because the admin should be able to action the order without first
 * navigating to its current-state page.
 */
export default function OrdersQuickSearch() {
  const [orderId, setOrderId] = useState("");
  const [clientId, setClientId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [email, setEmail] = useState("");
  const [rows, setRows] = useState(null);  // null = not searched yet
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [availableStates, setAvailableStates] = useState([]);

  // Modal hooks reused from per-state workflow.
  const tx = useTransitionModal({ onCommitted: () => runSearch() });
  const billing = useBillingModal();

  // Lazily load the full state catalogue once — needed for the transition
  // modal to show every legal target regardless of current state.
  useEffect(() => {
    adminApi.orderStates().then((r) => setAvailableStates(r.data || [])).catch(() => {});
  }, []);

  const onIdChange = (setter) => (e) => setter(e.target.value.replace(/[^0-9]/g, ""));
  const hasAny = Boolean(orderId || clientId || invoiceId || email.trim());

  const runSearch = async () => {
    if (!hasAny) return;
    setLoading(true); setErr("");
    try {
      const r = await adminApi.ordersQuickSearch({
        order_id: orderId, client_id: clientId, invoice_id: invoiceId, email: email.trim(),
      });
      setRows(r.data || []);
      setTotal(r.total || 0);
    } catch (ex) {
      setErr(ex.message || "Erro na pesquisa");
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  };

  const onSubmit = (e) => { e.preventDefault(); runSearch(); };

  const clear = () => {
    setOrderId(""); setClientId(""); setInvoiceId(""); setEmail("");
    setRows(null); setTotal(0); setErr("");
  };

  return (
    <section className="mb-6" data-testid="orders-quick-search">
      <div className="p-4 bg-white border border-[#E6E1D6] rounded-md">
        <div className="flex items-center gap-2 mb-3">
          <Search size={16} className="text-[#0F1829]" />
          <h2 className="text-sm font-bold uppercase tracking-[0.12em]">Pesquisa rápida por ID</h2>
          <span className="text-xs text-[#6B7280] ml-2">Procura em todos os estados.</span>
        </div>

        <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] text-[#6B7280] mb-1">ID Encomenda</label>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*"
              value={orderId} onChange={onIdChange(setOrderId)}
              data-testid="quick-order-id" placeholder="Ex: 70387"
              className="w-36 px-2 py-1.5 text-sm border border-[#E6E1D6] rounded font-mono focus:border-[#0F1829] outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] text-[#6B7280] mb-1">ID Cliente</label>
            <input
              type="text" inputMode="numeric" pattern="[0-9]*"
              value={clientId} onChange={onIdChange(setClientId)}
              data-testid="quick-client-id" placeholder="Ex: 128940"
              className="w-36 px-2 py-1.5 text-sm border border-[#E6E1D6] rounded font-mono focus:border-[#0F1829] outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] text-[#6B7280] mb-1">Nº / ID Fatura</label>
            <input
              type="text"
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value.toUpperCase())}
              data-testid="quick-invoice-id" placeholder="Ex: WEB2-27459"
              className="w-40 px-2 py-1.5 text-sm border border-[#E6E1D6] rounded font-mono uppercase focus:border-[#0F1829] outline-none"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[11px] text-[#6B7280] mb-1">Email cliente</label>
            <input
              type="search"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="quick-email" placeholder="Ex: joao.silva@example.com"
              className="w-full px-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#0F1829] outline-none"
            />
          </div>
          <button type="submit" disabled={!hasAny || loading}
            data-testid="quick-search-submit"
            className="px-4 py-1.5 text-sm rounded bg-[#0F1829] text-white hover:bg-[#1F2A44] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
            Procurar
          </button>
          {(rows !== null || hasAny) && (
            <button type="button" onClick={clear} data-testid="quick-clear"
              className="px-3 py-1.5 text-xs rounded border border-[#E6E1D6] hover:bg-[#FAF8F2] inline-flex items-center gap-1">
              <X size={12} /> Limpar
            </button>
          )}
        </form>

        {err && (
          <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded"
            data-testid="quick-search-error">{err}</div>
        )}

        {rows !== null && !loading && !err && (
          <div className="mt-4">
            <div className="text-xs text-[#6B7280] mb-2" data-testid="quick-search-total">
              {total} resultado(s)
            </div>
            {rows.length === 0 ? (
              <div className="text-sm text-[#6B7280] italic">Sem encomendas para os filtros indicados.</div>
            ) : (
              <div className="overflow-x-auto border border-[#E6E1D6] rounded">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF8F2] border-b border-[#E6E1D6]">
                    <tr>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">ID</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">Cliente</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">Data</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">Estado</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">Preço</th>
                      <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#978D78]">Acções</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.ID_Encomenda} data-testid={`quick-row-${r.ID_Encomenda}`}
                        className="border-b border-[#F3F0E7] hover:bg-[#FAF8F2]">
                        <td className="px-3 py-2 font-mono text-[#0F1829]">
                          <a href={`/encomendas/${r.ID_Encomenda}`} target="_blank" rel="noreferrer" className="hover:underline">
                            #{r.ID_Encomenda}
                          </a>
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{r.NomeCompleto || "—"}{r.ID_Cliente ? <span className="text-[#978D78] font-normal"> ({r.ID_Cliente})</span> : null}</div>
                          <div className="text-[11px] text-[#6B7280]">{r.Email || ""}</div>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-[#4B5563]">{(r.DataEncomenda || "").slice(0, 16)}</td>
                        <td className="px-3 py-2">
                          <span className="inline-block px-2 py-0.5 text-[11px] rounded bg-[#F5F2EB] border border-[#E6E1D6] font-medium"
                            data-testid={`quick-state-${r.ID_Encomenda}`} title={r.state_id}>
                            {r.state_label || r.state_id}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-right font-semibold text-[#0F1829] whitespace-nowrap">{EUR(r.PrecoFinal)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button onClick={() => tx.open(r)}
                            data-testid={`quick-transition-${r.ID_Encomenda}`}
                            className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-[#E6E1D6] bg-[#FAF8F2] hover:bg-[#0F1829] hover:text-white hover:border-[#0F1829] transition">
                            Alterar estado →
                          </button>
                          <button onClick={() => billing.open(r)}
                            data-testid={`quick-billing-${r.ID_Encomenda}`}
                            className="ml-2 inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-[#E6E1D6] bg-white hover:bg-[#F5F2EB] transition"
                            title="Faturação / documentos Moloni">
                            <FileText size={12} /> Faturação
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <TransitionModal
        order={tx.order} availableStates={availableStates}
        notes={tx.notes} setNotes={tx.setNotes}
        sendEmail={tx.sendEmail} setSendEmail={tx.setSendEmail}
        rastreamento={tx.rastreamento} setRastreamento={tx.setRastreamento}
        sending={tx.sending} onClose={tx.close} onTransition={tx.transition} onSendMessage={tx.sendMessage}
      />
      <BillingModal
        order={billing.order} data={billing.data} loading={billing.loading}
        emitting={billing.emitting} message={billing.message}
        onClose={billing.close} onEmit={billing.emitDoc} onRecalc={billing.recalcCommissions}
        onResendPrEmail={billing.resendPrEmail}
        onRefreshCofidis={billing.refreshCofidisLink}
      />
    </section>
  );
}
